import { useCallback, useEffect, useMemo, useState } from 'react'
import { ToastContainer, toast } from 'react-toastify';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { Dropdown, Empty, Flex, Radio, Select, Spin, Switch } from 'antd';
import { adminAddPlan, adminDeletePlan, adminGetPlans, adminUpdatePlan, UpdatePlanRecommended } from '../services/Api';

const defaultFormValues = {
  name: '',
  price: '',
  validity: '',
  description: '',
  validityType: 'months'// days, weeks, months,
};

const PlanManagement = () => {
  const token = localStorage.getItem('token');
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);


  const normalizedPlans = useMemo(() => {
    return (plans || []).map((plan) => {
      const rawValidity = Number(plan?.validity);
      const validityMonths =
        plan?.validityType === 'days' ? rawValidity : plan?.validityType === 'weeks' ? Math.round(rawValidity / 7) :
          Number.isFinite(rawValidity)
            ? rawValidity === 365
              ? 12
              : Math.round(rawValidity / 30)
            : '';
      return { ...plan, validityMonths };
    });
  }, [plans]);

  const getAllPlans = useCallback(() => {
    setIsLoading(true);
    adminGetPlans()
      .then((res) => {
        setPlans(res?.data?.data || []);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        toast(err?.response?.data?.message || 'Failed to fetch plans.');
      });
  }, []);

  useEffect(() => {
    getAllPlans();
  }, [token, getAllPlans]);

  const resetForm = () => {
    setFormValues(defaultFormValues);
    setEditingPlanId(null);
    setSelectedPlan(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setDialogType('form');
    setOpen(true);
  };

  const handleOpenEdit = (plan) => {
    setEditingPlanId(plan?._id);
    setSelectedPlan(plan);
    setFormValues({
      name: plan?.name || '',
      price: plan?.price ?? '',
      validity: plan?.validityMonths ?? '',
      description: plan?.description || '',
      validityType: plan?.validityType || "months"
    });
    setDialogType('form');
    setOpen(true);
  };

  const handleOpenDelete = (plan) => {
    setSelectedPlan(plan);
    setDialogType('delete');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDialogType('');
  };

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const name = String(formValues.name || '').trim();
    const description = String(formValues.description || '').trim();
    const price = Number(formValues.price);
    const validity = Number(formValues.validity);
    const validityType = String(formValues.validityType || 'months');

    if (!name || !description || !formValues.price || !formValues.validity || !validityType) {
      toast('All fields are required.');
      return false;
    }
    if (!Number.isFinite(price) || price <= 0) {
      toast('Price must be greater than 0.');
      return false;
    }
    // if (!Number.isFinite(validity) || validity < 1 || validity > 12) {
    //   toast('Validity must be between 1 and 12 months.');
    //   return false;
    // }
    if (validityType === 'days' && (validity < 1 || validity > 6)) {
      toast('Validity must be between 1 and 6 days.');
      return false;
    }
    if (validityType === 'weeks' && (validity < 1 || validity > 3)) {
      toast('Validity must be between 1 and 3 weeks.');
      return false;
    }
    if (validityType === 'months' && (validity < 1 || validity > 12)) {
      toast('Validity must be between 1 and 12 months.');
      return false;
    }
    console.log(validityType, validity);
    return { name, price, validity, description, validityType };
  };

  const handleSubmit = () => {
    const payload = validateForm();
    if (!payload) return;


    const request = editingPlanId
      ? adminUpdatePlan(editingPlanId, payload)
      : adminAddPlan(payload);


    request
      .then((res) => {
        toast(res?.data?.message || 'Plan saved successfully.');
        handleClose();
        resetForm();
        getAllPlans();
      })
      .catch((err) => {
        toast(err?.response?.data?.message || 'Failed to save plan.');
      });
  };

  const handleDelete = () => {
    if (!selectedPlan?._id) return;
    adminDeletePlan(selectedPlan._id)
      .then((res) => {
        toast(res?.data?.message || 'Plan deleted successfully.');
        handleClose();
        resetForm();
        getAllPlans();
      })
      .catch((err) => {
        toast(err?.response?.data?.message || 'Failed to delete plan.');
      });
  };

  const handleChangeRecommended = (plan) => {
    UpdatePlanRecommended(plan?._id)
      .then((res) => {
        toast(res?.data?.message || 'Plan updated successfully.');
        getAllPlans();
      })
      .catch((err) => {
        toast(err?.response?.data?.message || 'Failed to update plan.');
      });
  };

  return (
    <>
      <ToastContainer />
      <div className="space-y-6 px-5 py-5">
        <div className="flex justify-between items-center">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight text-[#3A4352]">Plan Management</h1>
          </div>
          <button
            className="bg-[#008400] text-white px-5 py-2 rounded-md active:scale-95 duration-500"
            onClick={handleOpenAdd}
          >
            Add Plan
          </button>
        </div>

        {isLoading ? (
          <div className='mt-20 flex justify-center items-center'>
            <Flex gap="middle" vertical>
              <Spin tip="Loading" size="large">
                <div className='p-12 rounded-xl'></div>
              </Spin>
            </Flex>
          </div>
        ) : (
          normalizedPlans.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/50 bg-[#FFF0F2]">
                    <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2 rounded-s-lg">S.No</th>
                    <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2">Plan Name</th>
                    <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2">Price</th>
                    <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2">Validity Type</th>
                    <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2">Validity</th>
                    <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold border-r-2">Description</th>
                    <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold rounded-e-lg">Recommended</th>
                    <th className="text-center text-[#394253] py-3 px-4 text-sm font-semibold rounded-e-lg">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {normalizedPlans.map((plan, index) => (
                    <tr key={plan?._id || index} className="border-b last:border-0">
                      <td className="py-3 px-4 text-sm text-[#62ABF4] text-center">{index + 1}</td>
                      <td className="py-3 px-4 text-sm text-[#394253] text-center">{plan?.name}</td>
                      <td className="py-3 px-4 text-sm text-[#394253] text-center">{plan?.price}</td>
                      <td className="py-3 px-4 text-sm text-[#394253] text-center">{plan?.validityType}</td>
                      <td className="py-3 px-4 text-sm text-[#394253] text-center">{plan?.validityMonths} {plan?.validityType === 'days' ? 'Day(s)' : plan?.validityType === 'weeks' ? 'Week(s)' : 'Month(s)'}</td>
                      <td className="py-3 px-4 text-sm text-[#394253] text-center">{plan?.description}</td>
                      <td className="py-3 px-4 text-sm text-[#394253] text-center">
                        <Radio
                          name='recommended'
                          checked={plan?.recommended}
                          onChange={() => handleChangeRecommended(plan)}
                        />
                      </td>
                      <td className="py-3 px-4 flex justify-center">
                        <div className="flex gap-2">
                          <button
                            className='border border-[#5E6977] text-[#5E6977] py-1 px-4 rounded-md'
                            onClick={() => handleOpenEdit(plan)}
                          >
                            Edit
                          </button>
                          <button
                            className='border border-[#E53935] text-white py-1 px-4 rounded-md'
                            onClick={() => handleOpenDelete(plan)}
                          >
                            <svg width="20" height="20" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M7.4754 19.0885C7.03021 19.0885 6.64989 18.9308 6.33445 18.6155C6.01916 18.3001 5.86151 17.9198 5.86151 17.4746V6.14344H4.96875V4.8043H8.98618V4.01465H14.3427V4.8043H18.3602V6.14344H17.4674V17.4746C17.4674 17.9256 17.3112 18.3073 16.9987 18.6198C16.6862 18.9322 16.3045 19.0885 15.8535 19.0885H7.4754ZM16.1283 6.14344H7.20065V17.4746C7.20065 17.5548 7.22639 17.6206 7.27788 17.6721C7.32936 17.7236 7.3952 17.7493 7.4754 17.7493H15.8535C15.9223 17.7493 15.9852 17.7207 16.0423 17.6634C16.0996 17.6063 16.1283 17.5433 16.1283 17.4746V6.14344ZM9.34685 15.9638H10.6858V7.92896H9.34685V15.9638ZM12.6431 15.9638H13.9821V7.92896H12.6431V15.9638Z" fill="#E53935" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty />
          )
        )}
      </div>

      <Dialog open={open} onClose={setOpen} className="relative">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-40 w-full sm:w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              {dialogType === 'form' && (
                <div>
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                        {editingPlanId ? 'Edit Plan' : 'Add New Plan'}
                      </DialogTitle>
                      <div className="mt-4">
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                          <div className='flex flex-col'>
                            <label className='text-gray-400 text-sm font-medium'>Plan Name</label>
                            <input
                              className='[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none outline-none ms-2 mt-1 border border-gray-400 h-8 ps-2 rounded-md w-[80%] placeholder:text-sm'
                              placeholder='Plan Name'
                              type='text'
                              value={formValues.name}
                              onChange={(e) => handleChange('name', e.target.value)}
                            />
                          </div>
                          <div className='flex flex-col'>
                            <label className='text-gray-400 text-sm font-medium'>Validity Type</label>
                            <Select
                              className='[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none outline-none ms-2 mt-1  h-8 ps-2 rounded-md w-[80%] placeholder:text-sm'
                              placeholder="Select validity type"
                              options={[
                                { label: 'Days', value: 'days' },
                                { label: 'Weeks', value: 'weeks' },
                                { label: 'Months', value: 'months' },
                              ]}
                              value={formValues.validityType}
                              onChange={(value) => handleChange("validityType", value)}
                            />
                          </div>
                          <div className='flex flex-col'>
                            <label className='text-gray-400 text-sm font-medium'>Price</label>
                            <input
                              className='[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none outline-none ms-2 mt-1 border border-gray-400 h-8 ps-2 rounded-md w-[80%] placeholder:text-sm'
                              placeholder='Price'
                              type='number'
                              min={1}
                              step="0.01"
                              value={formValues.price}
                              onChange={(e) => handleChange('price', e.target.value)}
                            />
                          </div>
                          <div className='flex flex-col'>
                            <label className='text-gray-400 text-sm font-medium'>Validity (Months)</label>
                            <input
                              className='[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none outline-none ms-2 mt-1 border border-gray-400 h-8 ps-2 rounded-md w-[80%] placeholder:text-sm'
                              placeholder={formValues.validityType === 'months' ? '1 - 12 Months' : formValues.validityType === 'days' ? '1 - 6 Days' : '1 - 3 Weeks'}
                              type='number'
                              min={1}
                              max={12}
                              value={formValues.validity}
                              onChange={(e) => handleChange('validity', e.target.value)}
                            />
                            <label className='text-gray-400 text-[12px] font-medium'>{

                              formValues.validityType === 'months' ? 'Validity can only be between 1 and 12 months' : formValues.validityType === 'days' ? 'Validity can only be between 1 and 6 days' : 'Validity can only be between 1 and 3 weeks'
                            }</label>
                          </div>
                          <div className='flex flex-col'>
                            <label className='text-gray-400 text-sm font-medium'>Description</label>
                            <textarea
                              className='[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none outline-none ms-2 mt-1 border border-gray-400 h-16 ps-2 rounded-md w-[80%] placeholder:text-sm'
                              placeholder='Plan Description or Features'
                              type='text'
                              value={formValues.description}
                              onChange={(e) => handleChange('description', e.target.value)}
                            />
                            <p className='text-gray-400 text-[10px] font-medium'>Comma(,) Separated to add multiple features</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="inline-flex w-full justify-center rounded-md bg-[#008400] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#007200] sm:ml-3 sm:w-auto">
                      {editingPlanId ? 'Update' : 'Save'}
                    </button>
                    <button
                      type="button"
                      data-autofocus
                      onClick={handleClose}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {dialogType === 'delete' && (
                <div>
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                        Delete Plan
                      </DialogTitle>
                      <p className="mt-2 text-sm text-gray-500">
                        Are you sure you want to delete {selectedPlan?.name || 'this plan'}?
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto">
                      Delete
                    </button>
                    <button
                      type="button"
                      data-autofocus
                      onClick={handleClose}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  )
}

export default PlanManagement